/**
 * NAVBAR - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { Fragment, useState, useEffect } from 'react';
import { Menu, Transition, Popover } from '@headlessui/react';
import { useNavigate, Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  UserCircleIcon,
  BookOpenIcon,
  ClipboardDocumentListIcon,
  FlagIcon,
  FolderIcon,
  BellIcon,
  Bars3Icon,
  ArrowRightOnRectangleIcon,
  GiftIcon,
  EnvelopeIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import TaskForm from '../forms/TaskForm';
import JournalForm from '../forms/JournalForm';
import GoalForm from '../forms/GoalForm';
import ProjectForm from '../forms/ProjectForm';
import WishListForm from '../forms/WishListForm';
import CronStatusIndicator from '../CronStatusIndicator';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../api/apiConfig';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';
import { useIsDesktop } from '../../hooks/useMediaQuery';

interface MenuItemProps {
  active: boolean;
}

interface NavbarProps {
  onOpenSidebar?: () => void;
}

interface NotificationItem {
  id: number;
  name?: string;
  description?: string;
  subject?: string;
  sender_name?: string;
  received_at?: string;
  due_date?: string;
  is_handled?: boolean;
  needs_internal_handling?: boolean;
  waiting_external_handling?: boolean;
}

interface NotificationGroup {
  type: string;
  title: string;
  count: number;
  priority: string;
  items: NotificationItem[];
}

const relativeTime = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
};

const overdueText = (dueDateStr: string): string => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((now.getTime() - due.getTime()) / 86400000);
  if (diffDays === 1) return '1 day overdue';
  return `${diffDays} days overdue`;
};

const truncate = (str: string, max: number): string =>
  str.length > max ? str.slice(0, max) + '...' : str;

const Navbar: React.FC<NavbarProps> = ({ onOpenSidebar }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isDesktop = useIsDesktop();
  const [searchQuery, setSearchQuery] = useState('');
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [isJournalFormOpen, setIsJournalFormOpen] = useState(false);
  const [isGoalFormOpen, setIsGoalFormOpen] = useState(false);
  const [isProjectFormOpen, setIsProjectFormOpen] = useState(false);
  const [isWishListFormOpen, setIsWishListFormOpen] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState<NotificationGroup[]>([]);
  const [notifCount, setNotifCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifError, setNotifError] = useState<string | null>(null);

  const fetchNotifications = async () => {
    setNotifLoading(true);
    setNotifError(null);

    try {
      const emailFilters = {
        is_archived: 'false',
        is_handled: 'false',
        is_newsletter: 'false',
        needs_internal_handling: 'false',
        waiting_external_handling: 'false',
      };

      // Fetch top 5 uncategorized emails (for preview items + count)
      const [emailsResponse, tasksResponse] = await Promise.all([
        apiClient.get('/api/emails/', {
          params: { ...emailFilters, page_size: 5 },
        }),
        apiClient.get('/api/tasks/'),
      ]);

      const emailCount = emailsResponse.data?.count || 0;
      const emailResults = emailsResponse.data?.results || [];

      // Process tasks
      const tasks = Array.isArray(tasksResponse.data) ? tasksResponse.data :
                   tasksResponse.data.results || tasksResponse.data.tasks || [];

      // Filter tasks to only include past due (before today, not including today)
      const filteredTasks = tasks.filter((task: any) => {
        if (!task.due_date || task.is_done) return false;
        const taskDate = new Date(task.due_date);
        const todayDate = new Date();
        todayDate.setHours(0, 0, 0, 0);
        return taskDate < todayDate;
      });

      // Create notification groups
      const notificationGroups: NotificationGroup[] = [];

      if (emailCount > 0) {
        notificationGroups.push({
          type: 'email',
          title: 'Uncategorized Emails',
          count: emailCount,
          priority: emailCount > 5 ? 'high' : 'medium',
          items: emailResults.slice(0, 5).map((email: any) => ({
            id: email.id,
            subject: email.subject,
            sender_name: email.sender_name || email.sender_email,
            received_at: email.received_at,
          })),
        });
      }

      if (filteredTasks.length > 0) {
        notificationGroups.push({
          type: 'task',
          title: 'Past Due Tasks',
          count: filteredTasks.length,
          priority: filteredTasks.length > 10 ? 'high' : 'medium',
          items: filteredTasks.slice(0, 5).map((task: any) => ({
            id: task.id,
            name: task.name,
            due_date: task.due_date,
          })),
        });
      }

      const totalCount = notificationGroups.reduce((sum, group) => sum + group.count, 0);

      setNotifications(notificationGroups);
      setNotifCount(totalCount);

    } catch (err) {
      console.error('Error fetching notifications:', err);
      setNotifError('Failed to load notifications.');
      setNotifications([]);
      setNotifCount(0);
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60_000); // refresh every 60s
    return () => clearInterval(interval);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();

    if (trimmedQuery) {
      const searchUrl = `/search?q=${encodeURIComponent(trimmedQuery)}`;
      navigate(searchUrl);
    }
  };

  const groupLink = (type: string) => {
    switch (type) {
      case 'email': return '/emails';
      case 'task': return '/tasks';
      default: return '#';
    }
  };

  const itemLink = (type: string, item: any) => {
    switch (type) {
      case 'email': return '/emails';
      case 'task': return `/tasks/${item.id}`;
      default: return '#';
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: isDesktop ? '112px' : '64px',
          padding: isDesktop ? `0 ${SPACING[24]}` : `0 ${SPACING[16]}`,
          backgroundColor: COLORS.bg.panel,
          borderBottom: `1px solid ${COLORS.border.default}`,
        }}
      >
        {/* Hamburger for mobile */}
        {!isDesktop && (
          <button
            style={{
              padding: SPACING[8],
              marginRight: SPACING[8],
              color: COLORS.text.secondary,
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: RADIUS,
              cursor: 'pointer',
              transition: 'all 0.1s ease',
            }}
            onClick={onOpenSidebar}
            aria-label="Open sidebar"
            onMouseEnter={(e) => {
              e.currentTarget.style.color = COLORS.accent.default;
              e.currentTarget.style.backgroundColor = COLORS.bg.hover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = COLORS.text.secondary;
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Bars3Icon style={{ width: '28px', height: '28px' }} />
          </button>
        )}
        {/* Search Bar */}
        <div style={{ flex: 1, maxWidth: '672px', margin: isDesktop ? `0 ${SPACING[32]}` : `0 ${SPACING[8]}` }}>
          <form onSubmit={handleSearch} style={{ position: 'relative' }}>
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                display: 'flex',
                alignItems: 'center',
                paddingLeft: SPACING[12],
                pointerEvents: 'none',
              }}
            >
              <MagnifyingGlassIcon style={{ width: '20px', height: '20px', color: COLORS.text.tertiary }} />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              style={{
                width: '100%',
                padding: `${SPACING[8]} ${SPACING[16]} ${SPACING[8]} ${SPACING[40]}`,
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                color: COLORS.text.primary,
                backgroundColor: COLORS.bg.base,
                border: `1px solid transparent`,
                borderRadius: RADIUS,
                outline: 'none',
                transition: 'all 0.1s ease',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = COLORS.accent.default;
                e.target.style.backgroundColor = COLORS.bg.panel;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'transparent';
                e.target.style.backgroundColor = COLORS.bg.base;
              }}
              placeholder="Search people, projects, businesses..."
            />
          </form>
        </div>

        {/* Cron Status Indicator (desktop only) */}
        {isDesktop && <CronStatusIndicator />}

        {/* Topbar Icons Grouped Right */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isDesktop ? SPACING[16] : SPACING[8], marginLeft: 'auto' }}>
          {/* Quick Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isDesktop ? SPACING[8] : SPACING[4] }} role="toolbar" aria-label="Quick actions">
            <button
              style={{
                padding: SPACING[8],
                color: COLORS.text.secondary,
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: RADIUS,
                cursor: 'pointer',
                transition: 'all 0.1s ease',
              }}
              title="Log Journal"
              onClick={() => setIsJournalFormOpen(true)}
              aria-label="Log Journal"
              onMouseEnter={(e) => {
                e.currentTarget.style.color = COLORS.accent.default;
                e.currentTarget.style.backgroundColor = COLORS.bg.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = COLORS.text.secondary;
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <BookOpenIcon style={{ width: '20px', height: '20px' }} aria-hidden="true" />
            </button>
            <button
              style={{
                padding: SPACING[8],
                color: COLORS.text.secondary,
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: RADIUS,
                cursor: 'pointer',
                transition: 'all 0.1s ease',
              }}
              title="Add Task"
              onClick={() => setIsTaskFormOpen(true)}
              aria-label="Add Task"
              onMouseEnter={(e) => {
                e.currentTarget.style.color = COLORS.accent.default;
                e.currentTarget.style.backgroundColor = COLORS.bg.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = COLORS.text.secondary;
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <ClipboardDocumentListIcon style={{ width: '20px', height: '20px' }} aria-hidden="true" />
            </button>
            <button
              style={{
                padding: SPACING[8],
                color: COLORS.text.secondary,
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: RADIUS,
                cursor: 'pointer',
                transition: 'all 0.1s ease',
              }}
              title="New Goal"
              onClick={() => setIsGoalFormOpen(true)}
              aria-label="New Goal"
              onMouseEnter={(e) => {
                e.currentTarget.style.color = COLORS.accent.default;
                e.currentTarget.style.backgroundColor = COLORS.bg.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = COLORS.text.secondary;
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <FlagIcon style={{ width: '20px', height: '20px' }} aria-hidden="true" />
            </button>
            <button
              style={{
                padding: SPACING[8],
                color: COLORS.text.secondary,
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: RADIUS,
                cursor: 'pointer',
                transition: 'all 0.1s ease',
              }}
              title="Add Project"
              onClick={() => setIsProjectFormOpen(true)}
              aria-label="Add Project"
              onMouseEnter={(e) => {
                e.currentTarget.style.color = COLORS.accent.default;
                e.currentTarget.style.backgroundColor = COLORS.bg.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = COLORS.text.secondary;
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <FolderIcon style={{ width: '20px', height: '20px' }} aria-hidden="true" />
            </button>
            <button
              style={{
                padding: SPACING[8],
                color: COLORS.text.secondary,
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: RADIUS,
                cursor: 'pointer',
                transition: 'all 0.1s ease',
              }}
              title="Add to Wish List"
              onClick={() => setIsWishListFormOpen(true)}
              aria-label="Add to Wish List"
              onMouseEnter={(e) => {
                e.currentTarget.style.color = COLORS.accent.default;
                e.currentTarget.style.backgroundColor = COLORS.bg.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = COLORS.text.secondary;
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <GiftIcon style={{ width: '20px', height: '20px' }} aria-hidden="true" />
            </button>
          </div>
          {/* Notification Bell */}
          <Popover style={{ position: 'relative' }}>
            {({ open }) => (
              <>
                <Popover.Button
                  style={{
                    position: 'relative',
                    padding: '6px',
                    borderRadius: '50%',
                    backgroundColor: open ? COLORS.bg.hover : 'transparent',
                    color: COLORS.text.tertiary,
                    border: 'none',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'all 0.1s ease',
                  }}
                  title={`View notifications (${notifCount})`}
                  aria-label={`View notifications (${notifCount})`}
                  aria-expanded={open}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = COLORS.text.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = COLORS.text.tertiary;
                  }}
                >
                  <span style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', borderWidth: 0 }}>View notifications</span>
                  <BellIcon style={{ width: '24px', height: '24px' }} aria-hidden="true" />
                  {/* Badge */}
                  {notifCount > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '2px 6px',
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.size.sm,
                        fontWeight: '700',
                        lineHeight: 1,
                        color: '#FEF2F2',
                        backgroundColor: '#DC2626',
                        borderRadius: '9999px',
                        border: `2px solid ${COLORS.bg.panel}`,
                      }}
                      aria-label={`${notifCount} unread notifications`}
                    >
                      {notifCount}
                    </span>
                  )}
                </Popover.Button>

                {/* Notification Panel */}
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Popover.Panel
                    style={{
                      position: 'absolute',
                      right: 0,
                      zIndex: 10,
                      marginTop: SPACING[8],
                      width: '320px',
                      transformOrigin: 'top right',
                      borderRadius: RADIUS,
                      backgroundColor: COLORS.bg.panel,
                      padding: `${SPACING[4]} 0`,
                      border: `1px solid ${COLORS.border.default}`,
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      outline: 'none',
                    }}
                    role="dialog"
                    aria-label="Notifications panel"
                  >
                    <div style={{ padding: `${SPACING[12]} ${SPACING[16]}`, borderBottom: `1px solid ${COLORS.border.default}` }}>
                      <p style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: '500', color: COLORS.text.primary }}>Notifications</p>
                    </div>
                    <div style={{ maxHeight: '420px', overflowY: 'auto' }} role="list">
                      {notifLoading ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: `${SPACING[16]} 0`,
                            color: COLORS.text.tertiary,
                          }}
                          role="status"
                          aria-label="Loading notifications"
                        >
                          <svg style={{ animation: 'spin 1s linear infinite', width: '20px', height: '20px', marginRight: SPACING[8] }} viewBox="0 0 24 24" aria-hidden="true">
                            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                          Loading...
                        </div>
                      ) : notifError ? (
                        <div style={{ padding: `${SPACING[8]} ${SPACING[16]}`, fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.status.error }} role="alert">{notifError}</div>
                      ) : notifications.length === 0 ? (
                        <div style={{ padding: `${SPACING[12]} ${SPACING[16]}`, fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }} role="status">No notifications.</div>
                      ) : (
                        notifications.map((group, idx) => (
                          <div
                            key={group.type || idx}
                            style={{
                              borderBottom: idx === notifications.length - 1 ? 'none' : `1px solid ${COLORS.border.default}`,
                            }}
                            role="listitem"
                          >
                            {/* Group header */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: `${SPACING[8]} ${SPACING[16]}`,
                              backgroundColor: COLORS.bg.base,
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                                {group.type === 'email' && <EnvelopeIcon style={{ width: '16px', height: '16px', color: COLORS.text.tertiary }} />}
                                {group.type === 'task' && <ClockIcon style={{ width: '16px', height: '16px', color: COLORS.text.tertiary }} />}
                                <span style={{
                                  fontFamily: TYPOGRAPHY.body.fontFamily,
                                  fontSize: TYPOGRAPHY.size.sm,
                                  fontWeight: '600',
                                  color: COLORS.text.primary,
                                }}>
                                  {group.title}
                                </span>
                                <span
                                  style={{
                                    fontFamily: TYPOGRAPHY.body.fontFamily,
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    borderRadius: '9999px',
                                    padding: '1px 6px',
                                    backgroundColor: group.priority === 'high' ? 'rgba(239, 68, 68, 0.15)' : group.priority === 'medium' ? 'rgba(245, 158, 11, 0.15)' : COLORS.bg.hover,
                                    color: group.priority === 'high' ? COLORS.status.error : group.priority === 'medium' ? COLORS.status.warning : COLORS.text.secondary,
                                  }}
                                  aria-label={`${group.count} items`}
                                >
                                  {group.count}
                                </span>
                              </div>
                              <Link
                                to={groupLink(group.type)}
                                style={{
                                  fontFamily: TYPOGRAPHY.body.fontFamily,
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  color: COLORS.accent.default,
                                  textDecoration: 'none',
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.accent.hover)}
                                onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.accent.default)}
                              >
                                View all →
                              </Link>
                            </div>
                            {/* Group items */}
                            {Array.isArray(group.items) && group.items.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {group.items.map((item: any) => (
                                  <Link
                                    key={item.id}
                                    to={itemLink(group.type, item)}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: SPACING[8],
                                      padding: `${SPACING[8]} ${SPACING[16]}`,
                                      textDecoration: 'none',
                                      color: 'inherit',
                                      transition: 'background-color 0.1s ease',
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.bg.hover)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                  >
                                    {group.type === 'email' && (
                                      <>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div style={{
                                            fontFamily: TYPOGRAPHY.body.fontFamily,
                                            fontSize: TYPOGRAPHY.size.sm,
                                            fontWeight: '500',
                                            color: COLORS.text.primary,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                          }}>
                                            {truncate(item.subject || '(no subject)', 40)}
                                          </div>
                                          <div style={{
                                            fontFamily: TYPOGRAPHY.body.fontFamily,
                                            fontSize: '11px',
                                            color: COLORS.text.tertiary,
                                          }}>
                                            {item.sender_name}
                                          </div>
                                        </div>
                                        <span style={{
                                          fontFamily: TYPOGRAPHY.body.fontFamily,
                                          fontSize: '11px',
                                          color: COLORS.text.tertiary,
                                          whiteSpace: 'nowrap',
                                          flexShrink: 0,
                                        }}>
                                          {item.received_at ? relativeTime(item.received_at) : ''}
                                        </span>
                                      </>
                                    )}
                                    {group.type === 'task' && (
                                      <>
                                        <div style={{
                                          flex: 1,
                                          minWidth: 0,
                                          fontFamily: TYPOGRAPHY.body.fontFamily,
                                          fontSize: TYPOGRAPHY.size.sm,
                                          fontWeight: '500',
                                          color: COLORS.text.primary,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}>
                                          {truncate(item.name || 'Untitled task', 40)}
                                        </div>
                                        <span style={{
                                          fontFamily: TYPOGRAPHY.body.fontFamily,
                                          fontSize: '11px',
                                          color: COLORS.status.error,
                                          whiteSpace: 'nowrap',
                                          flexShrink: 0,
                                        }}>
                                          {item.due_date ? overdueText(item.due_date) : ''}
                                        </span>
                                      </>
                                    )}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </Popover.Panel>
                </Transition>
              </>
            )}
          </Popover>
          {/* User Profile */}
          <Menu as="div" style={{ position: 'relative' }}>
            <Menu.Button
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: TYPOGRAPHY.body.sizes.small,
                borderRadius: '50%',
                outline: 'none',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
              }}
            >
              <UserCircleIcon style={{ width: '32px', height: '32px', color: COLORS.text.tertiary }} />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Menu.Items
                style={{
                  position: 'absolute',
                  right: 0,
                  marginTop: SPACING[8],
                  width: '224px',
                  transformOrigin: 'top right',
                  borderRadius: RADIUS,
                  backgroundColor: COLORS.bg.panel,
                  padding: `${SPACING[4]} 0`,
                  border: `1px solid ${COLORS.border.default}`,
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  outline: 'none',
                }}
              >
                {/* User Info */}
                <div style={{ padding: `${SPACING[12]} ${SPACING[16]}`, borderBottom: `1px solid ${COLORS.border.default}` }}>
                  <p style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: '500', color: COLORS.text.primary }}>
                    {user?.first_name && user?.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user?.username || 'User'
                    }
                  </p>
                  <p style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }}>{user?.email}</p>
                </div>

                <Menu.Item>
                  {({ active }: MenuItemProps) => (
                    <button
                      type="button"
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: `${SPACING[8]} ${SPACING[16]}`,
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        color: COLORS.text.primary,
                        backgroundColor: active ? COLORS.bg.hover : 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'background-color 0.1s ease',
                      }}
                    >
                      Your Profile
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }: MenuItemProps) => (
                    <button
                      type="button"
                      onClick={() => navigate('/settings')}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: `${SPACING[8]} ${SPACING[16]}`,
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        color: COLORS.text.primary,
                        backgroundColor: active ? COLORS.bg.hover : 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'background-color 0.1s ease',
                      }}
                    >
                      Settings
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }: MenuItemProps) => (
                    <button
                      type="button"
                      onClick={handleLogout}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        padding: `${SPACING[8]} ${SPACING[16]}`,
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        color: COLORS.text.primary,
                        backgroundColor: active ? COLORS.bg.hover : 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'background-color 0.1s ease',
                      }}
                    >
                      <ArrowRightOnRectangleIcon style={{ width: '16px', height: '16px', marginRight: SPACING[8] }} />
                      Sign out
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      <TaskForm
        isOpen={isTaskFormOpen}
        onClose={() => setIsTaskFormOpen(false)}
        onTaskCreated={() => {}}
      />
      <JournalForm
        isOpen={isJournalFormOpen}
        onClose={() => setIsJournalFormOpen(false)}
        onJournalEntryCreated={() => {}}
      />
      <GoalForm
        isOpen={isGoalFormOpen}
        onClose={() => setIsGoalFormOpen(false)}
        onGoalCreated={() => {}}
      />
      <ProjectForm
        isOpen={isProjectFormOpen}
        onClose={() => setIsProjectFormOpen(false)}
      />
      <WishListForm
        isOpen={isWishListFormOpen}
        onClose={() => setIsWishListFormOpen(false)}
        onItemCreated={() => {}}
      />
    </>
  );
};

export default Navbar;
